import { APP_INITIALIZER, NgModule } from '@angular/core';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TenantModule } from '@cloudia-shared//tenant/tenant.module';
import { CloudiaApisModule } from '@cloudia-shared/apis/cloudia-apis.module';
import { UserService } from '@cloudia-shared/apis/generated/user-api';
import { CanAccessModule } from '@cloudia-shared/directives/can-access/can-access.module';
import { tenantConfig } from '@cloudia-shared/tenant/tenant.config';
import { TenantService } from '@cloudia-shared/tenant/tenant.service';
import { environment } from '@envs/environment';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import {
  CV_TENANT_SERVICE,
  DROPDOWN_MODULES,
  TENANT_CONFIG,
} from '@tcp/cloudia-utilities';
import {
  GoogleTagManagerConfiguration,
  GoogleTagManagerModule,
  GoogleTagManagerService,
} from 'angular-google-tag-manager';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { document } from 'ngx-bootstrap/utils';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import '../moduleaugmentation';
import { FilePreviewOverlayModule } from '@cloudia-shared/components/file-preview-overlay/file-preview-overlay.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app/app.component';
import { UserSettingsInitializer } from './core/config/user-settings/user-settings.initializer';
import { ExternalConfigurationService } from './core/hal/external-configuration.service';
import { GlobalHttpInterceptor } from './core/interceptors/global-http-interceptor.service';
import { LocalBackendHttpInterceptor } from './core/interceptors/local-backend-http-interceptor.service';
import { SfCacheInterceptor } from './core/interceptors/sf-cache-interceptor.service';
import { CloudiaTitleStrategy } from './core/services/cloudia-title-strategy.service';
import { DropdownEssentialsModule } from './core/services/dropdown/dropdown-essentials.module';
import { DropdownModulesService } from './core/services/dropdown/dropdown-modules.service';
import { TranslocoRootModule } from './core/transloco/transloco-root.module';
import { DB_CONFIG } from './idb';

const initializeKeycloak =
  (
    keycloak: KeycloakService,
    userSettingsInitializer: UserSettingsInitializer,
    tagManagerConfig: GoogleTagManagerConfiguration
  ) =>
  async () => {
    if (document.location.pathname.includes('/csat/evaluation/')) {
      return;
    }
    await keycloak
      .init({
        config: {
          url: environment.kcUrl,
          realm: (environment.localBackend
            ? 'cloudia-local'
            : environment.kcRealm.cloudia
          ).replace(
            TenantService.cloudiaLegacyTenantName,
            new TenantService().getTenant()
          ),
          clientId: environment.kcClientID,
        },
        initOptions: {
          refreshToken: localStorage.getItem(
            new TenantService().getTenant() + '-refreshToken'
          ),
          checkLoginIframe: false,
        },
        bearerExcludedUrls: ['/assets/i18n'],
      })
      .then(async (auth) => {
        const R_TOKEN_KEY = new TenantService().getTenant() + '-refreshToken';

        if (auth) {
          const refreshToken = keycloak.getKeycloakInstance().refreshToken;
          if (refreshToken) {
            localStorage.setItem(R_TOKEN_KEY, refreshToken);
          }
        } else {
          const inst = keycloak.getKeycloakInstance();
          inst.refreshToken = localStorage.getItem(R_TOKEN_KEY);
          await inst.updateToken(4 * 3600).catch(() => inst.login());
        }
      })
      .then(() => userSettingsInitializer.init())
      .catch((reason) => {
        console.error(reason);
        document.location.href =
          document.location.origin +
          '/' +
          TenantService.cloudiaLegacyTenantName +
          document.location.pathname;
        Promise.resolve();
      });
  };

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserAnimationsModule,
    CanAccessModule,
    FilePreviewOverlayModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerImmediately',
    }),
    IonicModule.forRoot(),
    HttpClientModule,
    TranslocoRootModule,
    CloudiaApisModule,
    KeycloakAngularModule,
    NgxIndexedDBModule.forRoot(DB_CONFIG),
    TenantModule,
    NgxPaginationModule,
    DropdownEssentialsModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),
    GoogleTagManagerModule.forRoot({ id: environment.analyticsTrackingId }),
  ],
  providers: [
    {
      provide: 'ExternalConfigurationService',
      useClass: ExternalConfigurationService,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [
        KeycloakService,
        UserSettingsInitializer,
        UserService,
        TranslocoService,
        GoogleTagManagerService,
        ToastrService,
      ],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: environment.localBackend
        ? LocalBackendHttpInterceptor
        : GlobalHttpInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SfCacheInterceptor,
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: DROPDOWN_MODULES, useExisting: DropdownModulesService },
    {
      provide: TitleStrategy,
      useClass: CloudiaTitleStrategy,
    },
    { provide: CV_TENANT_SERVICE, useExisting: TenantService },
    { provide: TENANT_CONFIG, useValue: tenantConfig },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
